import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { ArrowLeft, Save, Loader2, Activity, Pencil, Trash2, X } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const TRACKING_STATUSES = [
  'INITIATE',
  'PICK UP',
  'IN TRANSIT',
  'DEPARTED',
  'ARRIVED',
  'DELIVERED',
  'COMPLETED',
  'UNDER CLEARANCE',
  'CUSTOM DELAY',
  'OUT OF DELIVERY',
  'RECEIVED AT ORIGIN HUB',
  'RECEIVED AT DEST HUB',
  'CLEARANCE COMPLETED',
  'CARTING DATE',
  'H/O To Airlines',
  'OTHERS',
];

const emptyForm = {
  from_location: '',
  to_location: '',
  status: '',
  date: new Date().toISOString().split('T')[0],
  remarks: '',
};

const TrackingForm = () => {
  const { docketId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [pageLoading, setPageLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [docketInfo, setDocketInfo] = useState(null);
  const [entries, setEntries] = useState([]);
  const [locations, setLocations] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const getAuthHeaders = () => {
    const token = localStorage.getItem('auth_token');
    return { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
  };

  const fetchEntries = async () => {
    const res = await fetch(`${backendUrl}/api/dockets/${docketId}/tracking`, {
      headers: getAuthHeaders()
    });
    if (res.ok) setEntries(await res.json());
  };

  useEffect(() => {
    const init = async () => {
      try {
        setPageLoading(true);
        // Fetch docket info
        const docketRes = await fetch(`${backendUrl}/api/dockets/${docketId}`, {
          headers: getAuthHeaders()
        });
        if (!docketRes.ok) throw new Error('Failed to fetch docket');
        const docketData = await docketRes.json();
        setDocketInfo(docketData);

        // Fetch locations for dropdowns
        const locRes = await fetch(`${backendUrl}/api/locations?limit=500`, {
          headers: getAuthHeaders()
        });
        if (locRes.ok) {
          const locData = await locRes.json();
          setLocations(Array.isArray(locData) ? locData.filter(l => l.is_active) : []);
        }

        await fetchEntries();
      } catch (err) {
        toast({ title: 'Error', description: err.message, variant: 'destructive' });
      } finally {
        setPageLoading(false);
      }
    };
    if (docketId) init();
  }, [docketId, backendUrl]);

  const handleChange = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    if (!form.status) {
      toast({ title: 'Validation', description: 'Status is required', variant: 'destructive' });
      return;
    }
    try {
      setSaving(true);
      let res;
      if (editingId) {
        res = await fetch(`${backendUrl}/api/dockets/${docketId}/tracking/${editingId}`, {
          method: 'PUT',
          headers: getAuthHeaders(),
          body: JSON.stringify(form)
        });
      } else {
        res = await fetch(`${backendUrl}/api/dockets/${docketId}/tracking`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify(form)
        });
      }
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to save');
      }
      toast({ title: 'Success', description: editingId ? 'Tracking entry updated' : 'Tracking entry saved' });
      setForm(emptyForm);
      setEditingId(null);
      await fetchEntries();
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (entry) => {
    setForm({
      from_location: entry.from_location || '',
      to_location: entry.to_location || '',
      status: entry.status || '',
      date: entry.date || new Date().toISOString().split('T')[0],
      remarks: entry.remarks || '',
    });
    setEditingId(entry.id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelEdit = () => {
    setForm(emptyForm);
    setEditingId(null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this tracking entry?')) return;
    try {
      const res = await fetch(`${backendUrl}/api/dockets/${docketId}/tracking/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (!res.ok) throw new Error('Failed to delete');
      toast({ title: 'Deleted', description: 'Tracking entry removed' });
      await fetchEntries();
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      return new Date(dateStr).toLocaleDateString('en-IN', {
        day: '2-digit', month: '2-digit', year: 'numeric'
      });
    } catch { return dateStr; }
  };

  const formatTime = (isoStr) => {
    if (!isoStr) return '—';
    try {
      return new Date(isoStr).toLocaleString('en-IN', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
      });
    } catch { return isoStr; }
  };

  const jobsheetNo = docketInfo?.system_docket_no || docketId;

  if (pageLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-accent-blue" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface-light p-6">
      <div className="max-w-5xl mx-auto space-y-6">

        {/* Page Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-xl font-bold text-text-dark font-manrope flex items-center gap-2">
              <Activity className="w-5 h-5 text-blue-600" />
              Update Tracking Status
            </h1>
            <p className="text-sm text-text-secondary font-ibm">
              Docket: {jobsheetNo}
            </p>
          </div>
        </div>

        {/* Form Card */}
        <Card className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600 to-blue-500 px-5 py-3 flex items-center justify-between">
            <h2 className="text-base font-semibold text-white font-ibm">
              {editingId ? 'Edit Tracking Entry' : 'Add Tracking Entry'}
            </h2>
            {editingId && (
              <Button size="sm" variant="ghost" className="text-white hover:bg-blue-700" onClick={handleCancelEdit}>
                <X className="w-4 h-4 mr-1" />
                Cancel Edit
              </Button>
            )}
          </div>

          <div className="p-5 space-y-4">

            {/* Row 1: Jobsheet No, From, To, Status */}
            <div className="grid grid-cols-4 gap-4">
              <div>
                <Label className="text-xs font-semibold text-text-secondary font-ibm">Jobsheet No.</Label>
                <Input
                  value={jobsheetNo}
                  readOnly
                  className="mt-1 bg-gray-50 cursor-not-allowed"
                  data-testid="tracking-jobsheet-no"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold text-text-secondary font-ibm">From</Label>
                <Select value={form.from_location} onValueChange={(v) => handleChange('from_location', v)}>
                  <SelectTrigger className="mt-1" data-testid="tracking-from">
                    <SelectValue placeholder="-- Select --" />
                  </SelectTrigger>
                  <SelectContent>
                    {locations.map((loc) => (
                      <SelectItem key={loc.id} value={loc.location_name}>
                        {loc.location_name}{loc.location_code ? ` (${loc.location_code})` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-semibold text-text-secondary font-ibm">To</Label>
                <Select value={form.to_location} onValueChange={(v) => handleChange('to_location', v)}>
                  <SelectTrigger className="mt-1" data-testid="tracking-to">
                    <SelectValue placeholder="-- Select --" />
                  </SelectTrigger>
                  <SelectContent>
                    {locations.map((loc) => (
                      <SelectItem key={loc.id} value={loc.location_name}>
                        {loc.location_name}{loc.location_code ? ` (${loc.location_code})` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-semibold text-text-secondary font-ibm">Status <span className="text-red-500">*</span></Label>
                <Select value={form.status} onValueChange={(v) => handleChange('status', v)}>
                  <SelectTrigger className="mt-1" data-testid="tracking-status">
                    <SelectValue placeholder="-- Select --" />
                  </SelectTrigger>
                  <SelectContent>
                    {TRACKING_STATUSES.map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Row 2: Date, Remarks */}
            <div className="grid grid-cols-4 gap-4">
              <div>
                <Label className="text-xs font-semibold text-text-secondary font-ibm">Date</Label>
                <Input
                  type="date"
                  value={form.date}
                  onChange={(e) => handleChange('date', e.target.value)}
                  className="mt-1"
                  data-testid="tracking-date"
                />
              </div>
              <div className="col-span-3">
                <Label className="text-xs font-semibold text-text-secondary font-ibm">Remarks</Label>
                <Textarea
                  value={form.remarks}
                  onChange={(e) => handleChange('remarks', e.target.value)}
                  placeholder="Optional remarks..."
                  className="mt-1 h-10"
                  data-testid="tracking-remarks"
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-green-600 hover:bg-green-700"
                data-testid="tracking-save-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                {saving ? 'Saving...' : editingId ? 'Update' : 'Save'}
              </Button>
              {editingId && (
                <Button variant="outline" onClick={handleCancelEdit} data-testid="tracking-cancel-btn">
                  Cancel
                </Button>
              )}
            </div>
          </div>
        </Card>

        {/* Tracking Entries Table */}
        <Card className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="bg-gray-50 border-b border-gray-200 px-5 py-3">
            <h2 className="text-sm font-semibold text-text-dark font-ibm">
              Tracking History ({entries.length} {entries.length === 1 ? 'entry' : 'entries'})
            </h2>
          </div>

          {entries.length === 0 ? (
            <div className="p-8 text-center text-text-secondary text-sm font-ibm">
              No tracking entries yet. Add the first entry above.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs font-ibm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-3 py-2 text-left text-text-secondary font-semibold">Sr. No</th>
                    <th className="px-3 py-2 text-left text-text-secondary font-semibold">Jobsheet No.</th>
                    <th className="px-3 py-2 text-left text-text-secondary font-semibold">From</th>
                    <th className="px-3 py-2 text-left text-text-secondary font-semibold">To</th>
                    <th className="px-3 py-2 text-left text-text-secondary font-semibold">Status</th>
                    <th className="px-3 py-2 text-left text-text-secondary font-semibold">Remarks</th>
                    <th className="px-3 py-2 text-left text-text-secondary font-semibold">Time</th>
                    <th className="px-3 py-2 text-left text-text-secondary font-semibold">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {entries.map((entry, idx) => (
                    <tr key={entry.id} className={`hover:bg-gray-50 transition-colors ${editingId === entry.id ? 'bg-blue-50' : ''}`}>
                      <td className="px-3 py-2 text-text-secondary">{idx + 1}</td>
                      <td className="px-3 py-2 font-medium text-text-dark">{entry.jobsheet_no}</td>
                      <td className="px-3 py-2 text-text-dark">{entry.from_location || '—'}</td>
                      <td className="px-3 py-2 text-text-dark">{entry.to_location || '—'}</td>
                      <td className="px-3 py-2">
                        <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full text-[10px] font-semibold">
                          {entry.status}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-text-secondary max-w-[160px] truncate">{entry.remarks || '—'}</td>
                      <td className="px-3 py-2 text-text-secondary whitespace-nowrap">{formatTime(entry.created_at)}</td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 w-7 p-0 text-blue-600 hover:bg-blue-50"
                            onClick={() => handleEdit(entry)}
                            data-testid={`tracking-edit-${idx}`}
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 w-7 p-0 text-red-500 hover:bg-red-50"
                            onClick={() => handleDelete(entry.id)}
                            data-testid={`tracking-delete-${idx}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

      </div>
    </div>
  );
};

export default TrackingForm;
